import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import {
  Bell,
  Buildings,
  CalendarDots,
  ChartBar,
  Check,
  FolderSimple,
  House,
  Lightbulb,
  Lightning,
  Moon,
  Notebook,
  Plus,
  SignOut,
  Sun,
  Ticket,
  User,
} from "@phosphor-icons/react";
import {
  ensureReferenceDataCached,
  fetchAllApplicantColleges,
  fetchApplicantProfile,
  isPremiumActive,
  hasPendingTryNowJourney,
  refreshSubscriptionState,
  signOutUser,
  startPremiumCheckout,
  useAuthState,
  useSubscriptionState,
} from "../../hallticket-lib";
import { UpdateProfileForm } from "../profile";
import { ResultsReview } from "./results/ResultsReview";
import { CollegesDirectory } from "./colleges/CollegesDirectory";
import { SpotRound } from "./spot-round";
import { useTheme } from "../../theme";
import { buildInfo } from "../../build/buildInfo";
import { PRO_PLANS_ENABLED } from "../../config/featureFlags";
import sidebarBrandIcon from "../../assets/design/HallTicket logo/app-icon/square-dark/hallticket-icon-256-dark.png";
import sidebarBrandWordmark from "../../assets/design/HallTicket logo/wordmark/transparent-ink/hallticket-wordmark-2048-ink.png";
import heroWatermarkIcon from "../../assets/design/HallTicket logo/app-icon/transparent/hallticket-icon-1024-transparent.png";
import "./Dashboard.css";

const NAV_MAIN = [
  { id: "dashboard", label: "Dashboard", icon: <House size={16} weight="duotone" /> },
  { id: "colleges", label: "College Search", icon: <Buildings size={16} weight="duotone" /> },
  { id: "results", label: "Results Review", icon: <ChartBar size={16} weight="duotone" /> },
  { id: "spot-round", label: "Spot Round", icon: <Ticket size={16} weight="duotone" /> },
];

const COMING_SOON_META: Record<string, { icon: React.ReactNode; desc: string }> = {
  exam: { icon: <Notebook size={56} weight="duotone" />, desc: "Track JEE, NEET, MHT-CET and more — schedules, admit cards, results, and everything in one place." },
  dockets: { icon: <FolderSimple size={56} weight="duotone" />, desc: "Store and organise all your admission documents — marksheets, certificates, and application copies." },
  calendar: { icon: <CalendarDots size={56} weight="duotone" />, desc: "A unified academic calendar synced with all major exam boards and counselling schedules." },
};

const PAGE_TITLE: Record<string, string> = {
  dashboard: "Dashboard",
  "action-items": "Action Items",
  results: "Results Review",
  exam: "Exam Tracker",
  colleges: "College Search",
  "spot-round": "Spot Round",
  dockets: "Dockets",
  calendar: "Calendar",
  profile: "Profile",
  alerts: "Alerts",
};

const PROFILE_COMPLETION_FIELDS = [
  { label: "Mobile verified", key: "mobile" },
  { label: "First name", key: "firstName" },
  { label: "Last name", key: "lastName" },
  { label: "Gender", key: "gender" },
  { label: "Date of birth", key: "dob" },
  { label: "Caste category", key: "category" },
  { label: "State of domicile", key: "state" },
  { label: "Address", key: "address" },
];

type DashboardPage = "dashboard" | "action-items" | "results" | "exam" | "colleges" | "spot-round" | "dockets" | "calendar" | "profile" | "alerts";

function getPageFromPath(pathname: string): DashboardPage {
  if (pathname === "/dashboard/results") {
    return "results";
  }
  if (pathname === "/dashboard/colleges") {
    return "colleges";
  }
  if (pathname === "/dashboard/spot-round") {
    return "spot-round";
  }
  if (pathname === "/dashboard/profile") {
    return "profile";
  }
  return "dashboard";
}

function isMobileBrowser(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const mobileMedia = window.matchMedia("(max-width: 900px)");
  const touchPoints = navigator.maxTouchPoints > 0;
  const mobileUserAgent = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|Mobile/i.test(navigator.userAgent);

  return mobileMedia.matches || (touchPoints && mobileUserAgent);
}

export function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleTheme } = useTheme();
  const authState = useAuthState();
  const subscriptionState = useSubscriptionState();
  const applicant = authState.applicant;
  const name = [applicant?.firstName, applicant?.lastName].filter(Boolean).join(" ").trim() || "Applicant";
  const firstName = applicant?.firstName?.trim() || "Applicant";
  const initials = `${applicant?.firstName?.[0] ?? "A"}${applicant?.lastName?.[0] ?? ""}`.toUpperCase();
  const subscription = subscriptionState.snapshot;
  const premiumEnabled = PRO_PLANS_ENABLED && isPremiumActive(subscription);
  const currentPlanLabel = premiumEnabled ? "Pro Plan" : "Free Plan";

  const [activePage, setActivePage] = React.useState<DashboardPage>(() => getPageFromPath(location.pathname));
  const [shortlistedCount, setShortlistedCount] = React.useState(0);
  const [taskFilter, setTaskFilter] = React.useState<"all" | "pending" | "done">("all");
  const [tasks, setTasks] = React.useState([
    { id: 1, name: "Register on MHT-CET portal", sub: "mahacet.org", priority: "high", due: "10 Apr 2026", done: false },
    { id: 2, name: "Upload Class XII Marksheet", sub: "Required for all applications", priority: "high", due: "15 Apr 2026", done: false },
    { id: 3, name: "Complete HallTicket Profile", sub: "Unlock personalized recommendations", priority: "medium", due: "20 Apr 2026", done: false },
    { id: 4, name: "Shortlist 10 Colleges", sub: "Use College Search", priority: "medium", due: "25 Apr 2026", done: false },
    { id: 5, name: "Download JEE Admit Card", sub: "jeemain.nta.nic.in", priority: "low", due: "28 Mar 2026", done: true },
  ] as Array<{ id: number; name: string; sub: string; priority: "high" | "medium" | "low"; due: string; done: boolean }>);
  const [alertRows, setAlertRows] = React.useState<Array<{ id: number; name: string; mobile: string; email: string }>>([]);
  const [isPremiumModalOpen, setIsPremiumModalOpen] = React.useState(false);
  const [isMobileWarningOpen, setIsMobileWarningOpen] = React.useState(false);
  const [checkoutStatusText, setCheckoutStatusText] = React.useState<string | null>(null);
  const [checkoutError, setCheckoutError] = React.useState<string | null>(null);
  const [isCheckoutInProgress, setIsCheckoutInProgress] = React.useState(false);
  const openPremiumUpgradeModal = React.useCallback(() => {
    setCheckoutError(null);
    setCheckoutStatusText(null);
    setIsPremiumModalOpen(true);
  }, []);

  const profileState = React.useMemo(() => {
    const state = applicant?.stateOfDomicile ?? applicant?.stateOfResidence ?? "";
    const selectedCategory = applicant?.verticalReservation ?? applicant?.category ?? "";
    return {
      mobile: Boolean(applicant?.mobileNo),
      firstName: Boolean(applicant?.firstName?.trim()),
      lastName: Boolean(applicant?.lastName?.trim()),
      gender: Boolean(applicant?.gender?.trim()),
      dob: Boolean(applicant?.dateOfBirth),
      category: Boolean(selectedCategory?.trim()),
      state: Boolean(state?.trim()),
      address: Boolean(applicant?.address?.trim() && applicant?.city?.trim() && applicant?.pinCode?.trim()),
    };
  }, [applicant]);

  const profileCompletion = React.useMemo(() => {
    const done = PROFILE_COMPLETION_FIELDS.filter((f) => profileState[f.key as keyof typeof profileState]).length;
    return Math.round((done / PROFILE_COMPLETION_FIELDS.length) * 100);
  }, [profileState]);
  const profileCompletionPending = PROFILE_COMPLETION_FIELDS.length - PROFILE_COMPLETION_FIELDS.filter((f) => profileState[f.key as keyof typeof profileState]).length;

  const pendingCount = tasks.filter((t) => !t.done).length;
  const filteredTasks = tasks.filter((t) => {
    if (taskFilter === "all") return true;
    if (taskFilter === "pending") return !t.done;
    return t.done;
  });

  const handleSignOut = async () => {
    await signOutUser();
    navigate("/signin", { replace: true });
  };

  const handleMainNavigation = (id: string) => {
    if (id === "results") {
      setActivePage("results");
      navigate("/dashboard/results");
      return;
    }
    if (id === "colleges") {
      setActivePage("colleges");
      navigate("/dashboard/colleges");
      return;
    }
    if (id === "spot-round") {
      setActivePage("spot-round");
      navigate("/dashboard/spot-round");
      return;
    }

    setActivePage("dashboard");
    navigate("/dashboard");
  };

  React.useEffect(() => {
    if (location.pathname === "/dashboard/results") {
      setActivePage("results");
      return;
    }
    if (location.pathname === "/dashboard/colleges") {
      setActivePage("colleges");
      return;
    }
    if (location.pathname === "/dashboard/spot-round") {
      setActivePage("spot-round");
      return;
    }
    if (location.pathname === "/dashboard/profile") {
      setActivePage("profile");
      return;
    }

    if (location.pathname === "/dashboard") {
      setActivePage("dashboard");
    }
  }, [location.pathname]);

  React.useEffect(() => {
    void ensureReferenceDataCached();
  }, []);

  React.useEffect(() => {
    if (location.pathname !== "/dashboard") {
      return;
    }
    if (!hasPendingTryNowJourney()) {
      return;
    }
    setActivePage("results");
    navigate("/dashboard/results", { replace: true });
  }, [location.pathname, navigate]);

  React.useEffect(() => {
    if (authState.status !== "signed_in" || !isMobileBrowser()) {
      return;
    }
    setIsMobileWarningOpen(true);
  }, [authState.status]);

  const refreshShortlistedCount = React.useCallback(async () => {
    try {
      const colleges = await fetchAllApplicantColleges();
      const preferred = colleges.filter((college) => college.preferred).length;
      setShortlistedCount(preferred);
    } catch {
      // Keep dashboard usable even if this metric fails.
    }
  }, []);

  React.useEffect(() => {
    if (activePage === "dashboard" || activePage === "colleges") {
      void refreshShortlistedCount();
    }
  }, [activePage, refreshShortlistedCount]);

  React.useEffect(() => {
    if (!PRO_PLANS_ENABLED) {
      return;
    }
    if (authState.status !== "signed_in" || subscriptionState.status !== "idle") {
      return;
    }

    void refreshSubscriptionState().catch(() => {
      // Keep dashboard usable even if subscription read fails.
    });
  }, [authState.status, subscriptionState.status]);

  const toggleTask = (id: number) =>
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));

  const onAlertChange = (id: number, key: "name" | "mobile" | "email", value: string) =>
    setAlertRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));

  const addAlertRow = () => {
    if (alertRows.length >= 3) return;
    setAlertRows((prev) => [...prev, { id: Date.now(), name: "", mobile: "", email: "" }]);
  };

  const removeAlertRow = (id: number) =>
    setAlertRows((prev) => prev.filter((r) => r.id !== id));

  React.useEffect(() => {
    if (!isPremiumModalOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isCheckoutInProgress) {
        setIsPremiumModalOpen(false);
        setCheckoutStatusText(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isCheckoutInProgress, isPremiumModalOpen]);

  const handleProceedToPayment = async () => {
    if (isCheckoutInProgress) {
      return;
    }

    setCheckoutError(null);
    setIsCheckoutInProgress(true);

    try {
      await startPremiumCheckout({
        prefill: {
          name,
          email: applicant?.email || undefined,
          contact: applicant?.mobileNo?.replace(/^\+91/, "") || undefined,
        },
        onStatusChange: (status) => {
          if (status === "creating_order") setCheckoutStatusText("Creating order...");
          if (status === "opening_checkout") setCheckoutStatusText("Opening Razorpay checkout...");
          if (status === "verifying_payment") setCheckoutStatusText("Verifying payment...");
          if (status === "refreshing_subscription") setCheckoutStatusText("Refreshing your dashboard...");
        },
      });

      await fetchApplicantProfile();
      setCheckoutStatusText("Payment verified. Loading latest plan...");
      setIsPremiumModalOpen(false);
      setCheckoutStatusText(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Payment could not be completed.";
      if (message !== "Checkout dismissed by user.") {
        setCheckoutError(message);
      }
      setCheckoutStatusText(null);
    } finally {
      setIsCheckoutInProgress(false);
    }
  };

  return (
    <div className="app-shell">
      {/* ── SIDEBAR ─────────────────────────── */}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-icon-sm"><img src={sidebarBrandIcon} alt="HallTicket" className="brand-icon-sm-img" /></div>
          <div className="sidebar-brand-name">
            <img src={sidebarBrandWordmark} alt="HallTicket" className="sidebar-brand-logo" />
          </div>
        </div>

        <div className="nav-profile-progress">
          <div className="pp-label">
            <span>Profile complete</span>
            <span className="pp-pct">{profileCompletion}%</span>
          </div>
          <div className="pp-track">
            <div className="pp-fill" style={{ width: `${profileCompletion}%` }} />
          </div>
        </div>

        <div className="nav-section">
          <div className="nav-label">Main</div>
          {NAV_MAIN.map(({ id, label, icon }) => (
            <button
              key={id}
              className={`nav-item${activePage === id ? " active" : ""}`}
              onClick={() => handleMainNavigation(id)}
            >
              <span className="nav-icon">{icon}</span>
              {label}
            </button>
          ))}
        </div>

        <div className="sidebar-spacer" />

        {PRO_PLANS_ENABLED && !premiumEnabled && (
          <div className="upgrade-cta">
            <div className="cta-emoji"><Lightning size={18} weight="duotone" /></div>
            <div className="cta-title">Unlock Pro</div>
            <div className="cta-sub">Decimal scores, alert recipients &amp; more</div>
            <button
              className="btn-upgrade"
              onClick={openPremiumUpgradeModal}
            >
              Upgrade — Rs49/yr
            </button>
          </div>
        )}

        <div className="nav-build-version" title={`Built on ${buildInfo.buildDateIso}`}>
          Build {buildInfo.buildVersion}
        </div>

        <div
          className="user-row"
          onClick={() => {
            setActivePage("profile");
            navigate("/dashboard/profile");
          }}
        >
          <div className="user-avatar">{initials}</div>
          <div className="user-info">
            <div className="user-name">{name}</div>
            {PRO_PLANS_ENABLED && (
              <div className={`user-plan${premiumEnabled ? " pro" : ""}`}>
                <span className="plan-dot" />
                {currentPlanLabel}
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* ── MAIN ────────────────────────────── */}
      <main className="main-shell">
        {/* Topbar */}
        <div className="topbar">
          <div className="topbar-left">
            <h2 className="topbar-title">{PAGE_TITLE[activePage]}</h2>
          </div>
          <div className="topbar-actions">
            <button type="button" className="theme-toggle-inline" onClick={toggleTheme} title="Toggle theme">
              {mode === "dark" ? <Sun size={16} weight="bold" /> : <Moon size={16} weight="bold" />}
            </button>
            <button type="button" className="logout-btn" onClick={() => void handleSignOut()}>
              <SignOut size={16} weight="bold" />
              Sign Out
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="content">

          {/* ── DASHBOARD ───────────────────── */}
          {activePage === "dashboard" && (
            <>
              <section className="welcome-hero">
                <div className="welcome-hero-watermark" aria-hidden="true">
                  <img src={heroWatermarkIcon} alt="" className="welcome-hero-watermark-img" />
                </div>
                <h1>Welcome, {firstName}.</h1>
                <p>Your personalised college admission companion. Track exams, analyse your chances, and stay ahead of every deadline.</p>
              </section>
              <section className="steps-grid">
                <article className="step-card" onClick={() => navigate("/dashboard/profile")}>
                  <div className="step-stat">
                    {profileCompletion}%<span className="step-stat-lbl">complete</span>
                  </div>
                  <div className="step-num">1</div>
                  <div className="step-icon"><User size={24} weight="duotone" /></div>
                  <div className="step-title">Complete Your Profile</div>
                  <div className="step-desc">Add your details and documents to unlock personalised recommendations.</div>
                </article>
                <article className="step-card" onClick={() => setActivePage("colleges")}>
                  <div className="step-stat">
                    {shortlistedCount}<span className="step-stat-lbl">shortlisted</span>
                  </div>
                  <div className="step-num">2</div>
                  <div className="step-icon"><Buildings size={24} weight="duotone" /></div>
                  <div className="step-title">Shortlist Colleges</div>
                  <div className="step-desc">Browse and shortlist colleges to see your admission probability.</div>
                </article>
                <article className="step-card" onClick={() => navigate("/dashboard/results")}>
                  <div className="step-num">3</div>
                  <div className="step-icon"><ChartBar size={24} weight="duotone" /></div>
                  <div className="step-title">Review Your Results</div>
                  <div className="step-desc">Enter your scores and see your admission probability across every college.</div>
                </article>
              </section>
              <div className="info-box">
                <div className="info-box-icon"><Lightbulb size={20} weight="duotone" /></div>
                <div>
                  <div className="info-box-title">Stay on track</div>
                  <div className="info-box-text">
                    Shortlist colleges, enter your scores in Results Review, and complete your Profile to get the most out of HallTicket.
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── ACTION ITEMS ─────────────────── */}
          {activePage === "action-items" && (
            <>
              <div className="task-summary">
                <div className="summary-card">
                  <div className="summary-val">{tasks.length}</div>
                  <div className="summary-lbl">Total Tasks</div>
                </div>
                <div className="summary-card">
                  <div className="summary-val" style={{ color: "var(--accent)" }}>{pendingCount}</div>
                  <div className="summary-lbl">Pending</div>
                </div>
                <div className="summary-card">
                  <div className="summary-val" style={{ color: "var(--success)" }}>
                    {tasks.filter((t) => t.done).length}
                  </div>
                  <div className="summary-lbl">Completed</div>
                </div>
              </div>
              <div className="action-header">
                <h2>Action Items</h2>
                <div className="action-filter">
                  {(["all", "pending", "done"] as const).map((f) => (
                    <button
                      key={f}
                      className={`filter-btn${taskFilter === f ? " active" : ""}`}
                      onClick={() => setTaskFilter(f)}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="task-table-wrap">
                <table className="task-table">
                  <thead>
                    <tr>
                      <th style={{ width: 44 }}>Done</th>
                      <th>Task</th>
                      <th>Priority</th>
                      <th>Due Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTasks.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", padding: 32, color: "var(--text-dim)" }}>
                          No tasks in this view.
                        </td>
                      </tr>
                    )}
                    {filteredTasks.map((task) => (
                      <tr key={task.id}>
                        <td>
                          <div className="task-toggle">
                            <input
                              type="checkbox"
                              id={`tc-${task.id}`}
                              checked={task.done}
                              onChange={() => toggleTask(task.id)}
                            />
                            <label htmlFor={`tc-${task.id}`} title={task.done ? "Mark pending" : "Mark done"}>
                              <Check size={12} weight="bold" aria-hidden="true" />
                            </label>
                          </div>
                        </td>
                        <td>
                          <div className={`task-name${task.done ? " done" : ""}`}>{task.name}</div>
                          <div className="task-sub">{task.sub}</div>
                        </td>
                        <td>
                          <span className={`priority-pill priority-${task.priority}`}>{task.priority}</span>
                        </td>
                        <td>
                          <span className="due-date">{task.due}</span>
                        </td>
                        <td>
                          <span className={`status-pill ${task.done ? "status-done" : "status-todo"}`}>
                            {task.done && <Check size={11} weight="bold" aria-hidden="true" />}
                            {task.done ? "Done" : "To Do"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ── RESULTS REVIEW ───────────────── */}
          {activePage === "results" && (
            <ResultsReview onUpgradeToPro={openPremiumUpgradeModal} />
          )}

          {/* ── COLLEGES ─────────────────────── */}
          {activePage === "colleges" && (
            <CollegesDirectory />
          )}

          {/* ── SPOT ROUND ───────────────────── */}
          {activePage === "spot-round" && (
            <SpotRound />
          )}

          {/* ── COMING SOON PAGES ────────────── */}
          {Object.keys(COMING_SOON_META).includes(activePage) && (
            <section className="coming-soon">
              <div className="cs-badge">Coming Soon</div>
              <div className="cs-icon">{COMING_SOON_META[activePage].icon}</div>
              <h2 className="cs-title">{PAGE_TITLE[activePage]}</h2>
              <p className="cs-desc">{COMING_SOON_META[activePage].desc}</p>
            </section>
          )}

          {/* ── PROFILE ──────────────────────── */}
          {activePage === "profile" && (
            <section className="profile-page">
              <div className="profile-progress-strip">
                <div className="profile-progress-strip__head">
                  <div>
                    <div className="profile-progress-strip__title">Profile complete</div>
                    <div className="profile-progress-strip__meta">
                      {profileCompletionPending === 0
                        ? "All key profile sections are completed."
                        : `${profileCompletionPending} section${profileCompletionPending === 1 ? "" : "s"} pending`}
                    </div>
                  </div>
                  <div className="profile-progress-strip__pct">{profileCompletion}%</div>
                </div>
                <div className="profile-progress-strip__track">
                  <div className="profile-progress-strip__fill" style={{ width: `${profileCompletion}%` }} />
                </div>
              </div>

              <div className="profile-grid">
                <div className="form-section">
                  <UpdateProfileForm />
                </div>
              </div>
            </section>
          )}

          {/* ── ALERTS ───────────────────────── */}
          {activePage === "alerts" && (
            <section className="alerts-section">
              <h2 className="alerts-heading">Alert Recipients</h2>
              <p className="alerts-sub">
                People listed here will receive copies of all alerts — exam updates, result announcements, and counselling reminders.
              </p>

              {PRO_PLANS_ENABLED && (
                <div className="pro-gate">
                  <div className="pro-gate-icon"><Lightning size={20} weight="duotone" /></div>
                  <div className="pro-gate-text">
                    <strong>Pro Feature</strong> — Alert Recipients is available on the Pro plan. You can add up to 3 recipients. Upgrade to unlock and keep your family informed automatically.
                  </div>
                  <button
                    className="btn-sm"
                    onClick={openPremiumUpgradeModal}
                    disabled={premiumEnabled}
                  >
                    {premiumEnabled ? "Pro is Active" : "Upgrade to Pro"}
                  </button>
                </div>
              )}

              <div className="alerts-count-row">
                <span className="alerts-count-label">Recipients</span>
                <span className={`count-pill${alertRows.length >= 3 ? " at-limit" : ""}`}>
                  {alertRows.length} / 3
                </span>
              </div>

              <div className="alerts-table-wrap">
                <table className="task-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Full Name</th>
                      <th>Mobile Number</th>
                      <th>Email Address</th>
                      <th style={{ width: 80 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alertRows.length === 0 && (
                      <tr>
                        <td colSpan={5}>
                          <div className="alerts-empty">
                            <div className="alerts-empty-icon"><Bell size={36} weight="duotone" /></div>
                            <div>No recipients added yet. Click <strong>+ Add Recipient</strong> below.</div>
                          </div>
                        </td>
                      </tr>
                    )}
                    {alertRows.map((row, idx) => (
                      <tr key={row.id}>
                        <td style={{ color: "var(--text-dim)", fontSize: 13 }}>{idx + 1}</td>
                        <td>
                          <InputText
                            className="inp p-inputtext-sm"
                            placeholder="Full Name"
                            value={row.name}
                            onChange={(e) => onAlertChange(row.id, "name", e.target.value)}
                          />
                        </td>
                        <td>
                          <InputText
                            className="inp p-inputtext-sm"
                            placeholder="10-digit mobile"
                            maxLength={10}
                            keyfilter="int"
                            value={row.mobile}
                            onChange={(e) => onAlertChange(row.id, "mobile", e.target.value)}
                          />
                        </td>
                        <td>
                          <InputText
                            className="inp p-inputtext-sm"
                            type="email"
                            placeholder="email@example.com"
                            value={row.email}
                            onChange={(e) => onAlertChange(row.id, "email", e.target.value)}
                          />
                        </td>
                        <td>
                          <button className="btn-remove" onClick={() => removeAlertRow(row.id)}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button className="btn-add-row" onClick={addAlertRow} disabled={alertRows.length >= 3}>
                <Plus size={14} weight="bold" />
                Add Recipient
              </button>
              <div className="alerts-limit-note">
                {PRO_PLANS_ENABLED ? "Maximum 3 recipients allowed on the Pro plan." : "Maximum 3 recipients allowed."}
              </div>
            </section>
          )}

        </div>{/* /content */}
      </main>

      {PRO_PLANS_ENABLED && isPremiumModalOpen && (
        <div
          className="modal-overlay open"
          onClick={() => {
            if (isCheckoutInProgress) return;
            setIsPremiumModalOpen(false);
            setCheckoutStatusText(null);
          }}
        >
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="modal-close"
              aria-label="Close upgrade modal"
              onClick={() => {
                if (isCheckoutInProgress) return;
                setIsPremiumModalOpen(false);
                setCheckoutStatusText(null);
              }}
            >
              x
            </button>
            <div className="modal-title">Upgrade to HallTicket Pro</div>
            <div className="modal-sub">Get unlimited access to all features for just Rs49/year.</div>
            <div className="plan-columns">
              <div className="plan-col">
                <div className="plan-col-header">
                  <div className="plan-col-name">Free</div>
                  <div className="plan-col-price">Rs0 <span>/ year</span></div>
                </div>
                <div className="plan-feature-list">
                  <div className="plan-feature"><span className="plan-feature-icon">✓</span> Dashboard &amp; action items</div>
                  <div className="plan-feature"><span className="plan-feature-icon">✓</span> College search &amp; shortlisting</div>
                  <div className="plan-feature"><span className="plan-feature-icon">✓</span> Results heatmap (whole numbers)</div>
                  <div className="plan-feature"><span className="plan-feature-icon">✓</span> Shortlist probability view</div>
                  <div className="plan-feature"><span className="plan-feature-icon">x</span> Decimal score precision</div>
                  <div className="plan-feature"><span className="plan-feature-icon">x</span> Alert recipients</div>
                  <div className="plan-feature"><span className="plan-feature-icon">x</span> Save &amp; track my results</div>
                </div>
              </div>
              <div className="plan-col pro-col">
                <div className="plan-col-header">
                  <div className="plan-col-name">Pro</div>
                  <div className="plan-col-price">Rs49 <span>/ year</span></div>
                </div>
                <div className="plan-feature-list">
                  <div className="plan-feature"><span className="plan-feature-icon">✓</span> Everything in Free</div>
                  <div className="plan-feature"><span className="plan-feature-icon">✓</span> Decimal score precision</div>
                  <div className="plan-feature"><span className="plan-feature-icon">✓</span> Up to 3 alert recipients</div>
                  <div className="plan-feature"><span className="plan-feature-icon">✓</span> Save &amp; track my results</div>
                </div>
              </div>
            </div>
            {checkoutStatusText && <div className="checkout-status">{checkoutStatusText}</div>}
            {checkoutError && <div className="checkout-error">{checkoutError}</div>}
            <button
              type="button"
              className="btn-upgrade modal-upgrade-btn"
              onClick={() => void handleProceedToPayment()}
              disabled={isCheckoutInProgress || premiumEnabled}
            >
              {premiumEnabled ? "Pro already active" : isCheckoutInProgress ? "Processing..." : "Proceed to Payment"}
            </button>
          </div>
        </div>
      )}

      {isMobileWarningOpen && (
        <div
          className="modal-overlay open"
          onClick={() => {
            setIsMobileWarningOpen(false);
          }}
        >
          <div className="modal-card mobile-warning-modal" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="modal-close"
              aria-label="Close mobile experience warning"
              onClick={() => setIsMobileWarningOpen(false)}
            >
              x
            </button>
            <div className="mobile-warning-title">⚠️ Limited Mobile Experience</div>
            <p className="mobile-warning-text">
              You’re currently using a mobile phone. Some important features of HallTicket work best on a laptop or desktop.
            </p>
            <p className="mobile-warning-text">
              To access the full decision-support experience and all tools, please switch to a larger screen.
            </p>
            <p className="mobile-warning-note">📱 Our mobile app is under development and will be released soon.</p>
            <button type="button" className="btn-upgrade mobile-warning-cta" onClick={() => setIsMobileWarningOpen(false)}>
              Continue on mobile
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
